import React from 'react';
import { FileStat } from '../api/IFileSystem';
import { useFileManagerStore } from '../store/useFileManagerStore';

const imageExtensions = new Set([
  '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.webp', '.tif', '.tiff', '.ico', '.svg'
]);

// Windows special folders that have custom icons
const specialFolderNames = new Set([
  'downloads', 'documents', 'pictures', 'music', 'videos', 'desktop',
  '下载', '文档', '图片', '音乐', '视频', '桌面',  // Chinese names
  'onedrive', 'dropbox', 'google drive', 'icloud drive',  // Cloud folders
  'program files', 'program files (x86)', 'windows', 'users', 'programdata',
  'appdata', 'local', 'roaming',
  '$recycle.bin', 'system volume information',
]);

// Check if a folder might have a custom icon
const isSpecialFolder = (name: string, path: string): boolean => {
  const lowerName = name.toLowerCase();
  // Check by name
  if (specialFolderNames.has(lowerName)) return true;
  // User profile folders (C:\Users\xxx\...)
  const lowerPath = path.toLowerCase();
  if (lowerPath.match(/^[a-z]:\\users\\[^\\]+\\[^\\]+$/)) return true;
  // Root level folders on drives
  if (lowerPath.match(/^[a-z]:\\[^\\]+$/)) return true;
  return false;
};

const iconCache = new Map<string, string>();
const pendingIconRequests = new Map<string, Promise<string | null>>();

const getExtension = (name: string) => {
  const idx = name.lastIndexOf('.');
  if (idx <= 0) return '';
  return name.slice(idx).toLowerCase();
};

const shouldCacheIcon = (file: FileStat) => {
  if (file.type !== 'file') return true;
  const ext = getExtension(file.name);
  if (ext === '.exe' || ext === '.lnk' || imageExtensions.has(ext)) return false;
  return true;
};

const getIconCacheKey = (file: FileStat) => {
  if (file.type === 'drive') return `path:${file.path}`;
  if (file.type === 'dir') {
    // Special folders with custom icons - cache by path
    if (isSpecialFolder(file.name, file.path)) {
      return `path:${file.path}`;
    }
    // Normal folders - share a common icon
    return 'type:dir';
  }
  if (file.type !== 'file') return 'file';
  const ext = getExtension(file.name);
  if (ext === '.exe' || ext === '.lnk' || imageExtensions.has(ext)) return `path:${file.path}`;
  return ext ? `ext:${ext}` : 'file';
};

export const useFileIcon = (file: FileStat, size: number) => {
  const fs = useFileManagerStore((state) => state.fs);
  const [iconUrl, setIconUrl] = React.useState<string | null>(() => {
    return file.iconData ? `data:image/png;base64,${file.iconData}` : null;
  });

  React.useEffect(() => {
    let cancelled = false;
    if (file.iconData) {
      setIconUrl(`data:image/png;base64,${file.iconData}`);
      return () => {
        cancelled = true;
      };
    }
    const key = `${size}:${getIconCacheKey(file)}`;
    const cached = iconCache.get(key);
    if (cached) {
      setIconUrl(cached);
      return () => {
        cancelled = true;
      };
    }
    const inFlight = pendingIconRequests.get(key);
    const request = inFlight ?? fs.getIcon(file.path, size)
      .then((res) => {
        if (!res?.data) return null;
        return `data:${res.mime};base64,${res.data}`;
      })
      .catch(() => null);
    pendingIconRequests.set(key, request);
    request.then((data) => {
      pendingIconRequests.delete(key);
      if (data && shouldCacheIcon(file)) {
        iconCache.set(key, data);
      }
      if (!cancelled && data) {
        setIconUrl(data);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [file.path, file.name, file.type, file.iconData, size, fs]);

  return iconUrl;
};
